import React, { createContext, useContext, useState, useEffect } from 'react'
import authService from '../api/authService'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true;
    
    // Check if user is already logged in (from token)
    const token = localStorage.getItem('token')
    
    if (token) {
      // Verify token and get user data
      authService.getMe()
        .then(response => {
          if (isMounted) {
            setUser(response.data.user)
            setIsAuthenticated(true)
            setLoading(false)
          }
        })
        .catch(() => {
          if (isMounted) {
            // Token invalid, clear auth data
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            setLoading(false)
          }
        })
    } else {
      if (isMounted) {
        setLoading(false)
      }
    }

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [])

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password)
      
      if (response.status === 'success') {
        // Backend returns: { status, token, refreshToken, data: { user } }
        const { token, refreshToken, data } = response
        const { user } = data
        
        // Save token and user to localStorage
        localStorage.setItem('token', token)
        localStorage.setItem('refreshToken', refreshToken)
        localStorage.setItem('user', JSON.stringify(user))
        
        setUser(user)
        setIsAuthenticated(true)
        
        return { success: true, user }
      } else {
        return { success: false, message: response.message || 'Login failed' }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed. Please try again.' 
      }
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear local state regardless of API call result
      setUser(null)
      setIsAuthenticated(false)
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
    }
  }

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}